/**
 * Transfers Table Component
 *
 * DataGrid for transfers list.
 * @see Sprint 14 - Transferts Inter-Sites
 * @see US-14.5 - UI Liste et Creation
 */

import { useMemo, useCallback } from 'react';
import {
  DataGrid,
  Badge,
  textColumn,
  dateColumn,
  moneyColumn,
  integerColumn,
  type DataGridColumn,
  type DataGridSortState,
} from '@bill/ui';
import type { TransferListItem, PaginationInfo, QueryTransfersDto } from '@bstock/shared';
import { TRANSFER_STATUS_COLORS, getTransferStatusLabel, isDestinationSite } from '@bstock/shared';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes } from '@/lib/format';

interface TransfersTableProps {
  transfers: TransferListItem[];
  pagination: PaginationInfo;
  isLoading: boolean;
  sortBy?: QueryTransfersDto['sortBy'];
  sortOrder?: QueryTransfersDto['sortOrder'];
  currentSiteId?: string;
  onRowClick: (transfer: TransferListItem) => void;
  onSort: (field: QueryTransfersDto['sortBy'], order: QueryTransfersDto['sortOrder']) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onFetchAllRows?: () => Promise<TransferListItem[]>;
}

const SORT_FIELD_MAP: Record<string, NonNullable<QueryTransfersDto['sortBy']>> = {
  transferNumber: 'transferNumber',
  totalValueCentimes: 'totalValueCentimes',
  shippedAt: 'shippedAt',
  receivedAt: 'receivedAt',
  createdAt: 'createdAt',
};

export function TransfersTable({
  transfers,
  pagination,
  isLoading,
  sortBy = 'createdAt',
  sortOrder = 'desc',
  currentSiteId,
  onRowClick,
  onSort,
  onPageChange,
  onLimitChange,
  onFetchAllRows,
}: TransfersTableProps) {
  const currency = useCurrency();
  const { formatDateShort } = useFormatDate();

  const columns = useMemo<DataGridColumn<TransferListItem>[]>(
    () => [
      textColumn<TransferListItem>({
        id: 'transferNumber',
        header: 'Numero',
        accessor: 'transferNumber',
        cell: (r) => (
          <div>
            <div className="font-medium text-primary-600">{r.transferNumber}</div>
            <div className="text-xs text-neutral-400 mt-0.5">
              {formatDateShort(r.createdAt)}
            </div>
          </div>
        ),
        exportValue: (r) => r.transferNumber,
      }),
      textColumn<TransferListItem>({
        id: 'status',
        header: 'Statut',
        sortable: false,
        accessor: (r) => {
          const isRequester = currentSiteId ? isDestinationSite(r, currentSiteId) : true;
          return getTransferStatusLabel(r.status, isRequester);
        },
        cell: (r) => {
          const isRequester = currentSiteId ? isDestinationSite(r, currentSiteId) : true;
          return (
            <Badge variant={TRANSFER_STATUS_COLORS[r.status]}>
              {getTransferStatusLabel(r.status, isRequester)}
            </Badge>
          );
        },
      }),
      textColumn<TransferListItem>({
        id: 'sourceSite',
        header: 'Fournisseur',
        sortable: false,
        accessor: (r) => r.sourceSite.name,
        cell: (r) => (
          <div>
            <div className="font-medium text-neutral-800">{r.sourceSite.name}</div>
            {r.sourceSite.city && (
              <div className="text-xs text-neutral-400">{r.sourceSite.city}</div>
            )}
          </div>
        ),
      }),
      textColumn<TransferListItem>({
        id: 'destinationSite',
        header: 'Demandeur',
        sortable: false,
        accessor: (r) => r.destinationSite.name,
        cell: (r) => (
          <div>
            <div className="font-medium text-neutral-800">{r.destinationSite.name}</div>
            {r.destinationSite.city && (
              <div className="text-xs text-neutral-400">{r.destinationSite.city}</div>
            )}
          </div>
        ),
      }),
      integerColumn<TransferListItem>({
        id: 'lineCount',
        header: 'Lignes',
        sortable: false,
        align: 'center',
        accessor: 'lineCount',
      }),
      moneyColumn<TransferListItem>({
        id: 'totalValueCentimes',
        header: 'Valeur',
        accessor: (r) => r.totalValueCentimes / 100,
        cell: (r) => (
          <span className="font-medium">
            {r.totalValueCentimes > 0 ? formatCentimes(r.totalValueCentimes, currency) : '-'}
          </span>
        ),
        exportValue: (r) => r.totalValueCentimes / 100,
      }),
      dateColumn<TransferListItem>({
        id: 'shippedAt',
        header: 'Expedition',
        accessor: (r) => r.shippedAt ?? null,
        cell: (r) => r.shippedAt ? formatDateShort(r.shippedAt) : '-',
        exportValue: (r) => r.shippedAt ?? null,
      }),
      dateColumn<TransferListItem>({
        id: 'receivedAt',
        header: 'Reception',
        accessor: (r) => r.receivedAt ?? null,
        cell: (r) => r.receivedAt ? formatDateShort(r.receivedAt) : '-',
        exportValue: (r) => r.receivedAt ?? null,
      }),
    ],
    [currentSiteId, currency, formatDateShort]
  );

  const sortState = useMemo<DataGridSortState | null>(() => {
    if (!sortBy || !sortOrder) return null;
    const columnId = Object.entries(SORT_FIELD_MAP).find(([, v]) => v === sortBy)?.[0];
    if (!columnId) return null;
    return { columnId, direction: sortOrder };
  }, [sortBy, sortOrder]);

  const handleSortChange = useCallback(
    (next: DataGridSortState | null) => {
      if (!next) {
        onSort('createdAt', 'desc');
        return;
      }
      const backendField = SORT_FIELD_MAP[next.columnId];
      if (backendField) onSort(backendField, next.direction);
    },
    [onSort]
  );

  return (
    <DataGrid<TransferListItem>
      data={transfers}
      columns={columns}
      rowKey={(r) => r.id}
      isLoading={isLoading}
      skeletonRows={8}
      onRowClick={onRowClick}
      currency={currency}
      sortState={sortState}
      onSortChange={handleSortChange}
      exportable={{
        fileName: { base: 'transferts' },
        sheetName: 'Transferts',
        position: 'header',
        onFetchAllRows,
      }}
      pagination={{
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        totalPages: pagination.totalPages,
        onPageChange,
        onLimitChange,
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucun transfert
        </div>
      }
    />
  );
}
